import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth';
import { LogOut, Plus, Trash2, Pencil, X, Save } from 'lucide-react';
import api from '../../lib/api';

const SECTIONS = [
  {
    key: 'articles', label: 'admin.articles',
    fields: [
      { name: 'title_fr', label: 'Titre FR', type: 'text', required: true },
      { name: 'title_en', label: 'Title EN', type: 'text', required: true },
      { name: 'excerpt_fr', label: 'Résumé FR', type: 'textarea' },
      { name: 'excerpt_en', label: 'Excerpt EN', type: 'textarea' },
      { name: 'body_fr', label: 'Contenu FR', type: 'textarea' },
      { name: 'body_en', label: 'Body EN', type: 'textarea' },
      { name: 'cover_image', label: 'Image (URL ou upload)', type: 'image' },
      { name: 'category', label: 'Catégorie', type: 'select', options: ['actualite', 'evenement', 'projet'] },
      { name: 'published', label: 'Publié', type: 'checkbox', default: true },
    ],
  },
  {
    key: 'products', label: 'admin.products',
    fields: [
      { name: 'name_fr', label: 'Nom FR', type: 'text', required: true },
      { name: 'name_en', label: 'Name EN', type: 'text', required: true },
      { name: 'description_fr', label: 'Description FR', type: 'textarea' },
      { name: 'description_en', label: 'Description EN', type: 'textarea' },
      { name: 'benefits_fr', label: 'Bienfaits FR (un par ligne)', type: 'list' },
      { name: 'benefits_en', label: 'Benefits EN (one per line)', type: 'list' },
      { name: 'image', label: 'Image (URL ou upload)', type: 'image' },
      { name: 'certifications', label: 'Certifications (séparées par virgule)', type: 'csv' },
      { name: 'price_eur', label: 'Prix EUR (laisser 0 si non vendu)', type: 'number' },
      { name: 'weight_label', label: 'Poids / volume (ex: 100g)', type: 'text' },
      { name: 'stock', label: 'Stock (-1 = illimité)', type: 'number' },
      { name: 'available', label: 'Disponible à la vente', type: 'checkbox', default: true },
    ],
  },
  {
    key: 'formations', label: 'admin.formations',
    fields: [
      { name: 'title_fr', label: 'Titre FR', type: 'text', required: true },
      { name: 'title_en', label: 'Title EN', type: 'text', required: true },
      { name: 'description_fr', label: 'Description FR', type: 'textarea' },
      { name: 'description_en', label: 'Description EN', type: 'textarea' },
      { name: 'duration', label: 'Durée', type: 'text' },
      { name: 'image', label: 'Image (URL ou upload)', type: 'image' },
      { name: 'topics_fr', label: 'Thèmes FR (un par ligne)', type: 'list' },
      { name: 'topics_en', label: 'Topics EN (one per line)', type: 'list' },
    ],
  },
  {
    key: 'partners', label: 'admin.partners',
    fields: [
      { name: 'name', label: 'Nom', type: 'text', required: true },
      { name: 'description_fr', label: 'Description FR', type: 'textarea' },
      { name: 'description_en', label: 'Description EN', type: 'textarea' },
      { name: 'logo', label: 'Logo (URL ou upload)', type: 'image' },
      { name: 'website', label: 'Site web', type: 'text' },
    ],
  },
  {
    key: 'gallery', label: 'admin.gallery',
    fields: [
      { name: 'image', label: 'Image (URL ou upload)', type: 'image', required: true },
      { name: 'caption_fr', label: 'Légende FR', type: 'text' },
      { name: 'caption_en', label: 'Caption EN', type: 'text' },
      { name: 'category', label: 'Catégorie', type: 'select', options: ['general', 'farm', 'products', 'events', 'training'] },
    ],
  },
  {
    key: 'experiences', label: 'admin.experiences',
    fields: [
      { name: 'title_fr', label: 'Titre FR', type: 'text', required: true },
      { name: 'title_en', label: 'Title EN', type: 'text', required: true },
      { name: 'summary_fr', label: 'Résumé FR', type: 'textarea' },
      { name: 'summary_en', label: 'Summary EN', type: 'textarea' },
      { name: 'description_fr', label: 'Description FR', type: 'textarea' },
      { name: 'description_en', label: 'Description EN', type: 'textarea' },
      { name: 'image', label: 'Image (URL ou upload)', type: 'image' },
      { name: 'duration', label: 'Durée', type: 'text' },
      { name: 'price', label: 'Prix', type: 'text' },
      { name: 'capacity', label: 'Capacité', type: 'text' },
      { name: 'category', label: 'Catégorie', type: 'select', options: ['visit', 'workshop', 'immersion', 'corporate'] },
      { name: 'includes_fr', label: 'Inclus FR (un par ligne)', type: 'list' },
      { name: 'includes_en', label: 'Includes EN (one per line)', type: 'list' },
      { name: 'audience_fr', label: 'Public visé FR', type: 'text' },
      { name: 'audience_en', label: 'Audience EN', type: 'text' },
      { name: 'published', label: 'Publié', type: 'checkbox', default: true },
    ],
  },
  {
    key: 'projects', label: 'admin.projects',
    fields: [
      { name: 'title_fr', label: 'Titre FR', type: 'text', required: true },
      { name: 'title_en', label: 'Title EN', type: 'text', required: true },
      { name: 'description_fr', label: 'Description FR', type: 'textarea' },
      { name: 'description_en', label: 'Description EN', type: 'textarea' },
      { name: 'image', label: 'Image (URL ou upload)', type: 'image' },
      { name: 'year', label: 'Année', type: 'text' },
      { name: 'status', label: 'Statut', type: 'select', options: ['ongoing', 'completed'] },
    ],
  },
  {
    key: 'stats', label: 'admin.stats',
    fields: [
      { name: 'key', label: 'Clé technique (years, tons, certifications, ...)', type: 'text', required: true },
      { name: 'value', label: 'Valeur numérique', type: 'number', required: true },
      { name: 'suffix', label: 'Suffixe (ex: "+", "t")', type: 'text' },
      { name: 'label_fr', label: 'Libellé FR', type: 'text' },
      { name: 'label_en', label: 'Label EN', type: 'text' },
      { name: 'order', label: "Ordre d'affichage", type: 'number' },
    ],
  },
  {
    key: 'sponsorships', label: 'admin.sponsorships',
    fields: [
      { name: 'key', label: 'Clé technique (baobab, hectare, ...)', type: 'text', required: true },
      { name: 'name_fr', label: 'Nom FR', type: 'text', required: true },
      { name: 'name_en', label: 'Name EN', type: 'text', required: true },
      { name: 'description_fr', label: 'Description FR', type: 'textarea' },
      { name: 'description_en', label: 'Description EN', type: 'textarea' },
      { name: 'price_eur', label: 'Prix EUR', type: 'number', required: true },
      { name: 'duration_label_fr', label: 'Sous-titre prix FR', type: 'text' },
      { name: 'duration_label_en', label: 'Price subtitle EN', type: 'text' },
      { name: 'perks_fr', label: 'Avantages FR (un par ligne)', type: 'list' },
      { name: 'perks_en', label: 'Perks EN (one per line)', type: 'list' },
      { name: 'image', label: 'Image (URL ou upload)', type: 'image' },
      { name: 'highlighted', label: 'Mis en avant', type: 'checkbox' },
      { name: 'order', label: "Ordre d'affichage", type: 'number' },
      { name: 'available', label: 'Disponible', type: 'checkbox', default: true },
    ],
  },
];

const buildEmpty = (fields) => {
  const obj = {};
  fields.forEach((f) => {
    if (f.type === 'list') obj[f.name] = [];
    else if (f.type === 'csv') obj[f.name] = [];
    else if (f.type === 'checkbox') obj[f.name] = f.default ?? false;
    else obj[f.name] = '';
  });
  return obj;
};

const toFormValue = (f, v) => {
  if (f.type === 'list') return Array.isArray(v) ? v.join('\n') : '';
  if (f.type === 'csv') return Array.isArray(v) ? v.join(', ') : '';
  return v ?? '';
};

const fromFormValue = (f, v) => {
  if (f.type === 'list') return String(v || '').split('\n').map((s) => s.trim()).filter(Boolean);
  if (f.type === 'csv') return String(v || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (f.type === 'checkbox') return !!v;
  if (f.type === 'number') return v === '' || v === null ? 0 : Number(v);
  return v;
};

function ImageField({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.url);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };
  const fullUrl = value && value.startsWith('/api/uploads/') ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;
  return (
    <div className="grid gap-2">
      <input
        type="text"
        placeholder="https://... ou /api/uploads/..."
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-border-soft bg-white px-4 py-2.5 outline-none focus:border-forest text-sm"
        data-testid={`${testid}-url`}
      />
      <div className="flex items-center gap-3">
        <label className="inline-flex items-center gap-2 px-3 py-2 border border-border-soft rounded-lg bg-bone hover:bg-sand text-sm cursor-pointer">
          <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testid}-file`} />
          {busy ? 'Upload…' : 'Téléverser une image'}
        </label>
        {value && (
          <div className="w-16 h-16 rounded-lg overflow-hidden border border-border-soft bg-white">
            <img src={fullUrl} alt="preview" className="w-full h-full object-cover" />
          </div>
        )}
      </div>
      {err && <p className="text-red-700 text-xs">{err}</p>}
    </div>
  );
}

function EditModal({ section, item, onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState(() => {
    const base = buildEmpty(section.fields);
    if (item) {
      section.fields.forEach((f) => {
        base[f.name] = toFormValue(f, item[f.name]);
      });
    }
    return base;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const payload = {};
      section.fields.forEach((f) => { payload[f.name] = fromFormValue(f, form[f.name]); });
      if (isEdit) await api.put(`/admin/${section.key}/${item.id}`, payload);
      else await api.post(`/admin/${section.key}`, payload);
      onSaved();
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm grid place-items-center p-4 overflow-auto" onClick={onClose} data-testid="edit-modal">
      <form onSubmit={submit} className="bg-bone rounded-3xl max-w-2xl w-full my-12 p-8 grid gap-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="font-heading text-2xl text-ink">{isEdit ? 'Modifier' : 'Ajouter'}</h3>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink"><X /></button>
        </div>
        {section.fields.map((f) => (
          <div key={f.name} className="grid gap-1.5">
            <label className="overline">{f.label}</label>
            {f.type === 'textarea' || f.type === 'list' ? (
              <textarea
                rows={f.type === 'list' ? 4 : 5}
                required={f.required}
                value={form[f.name]}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                className="rounded-xl border border-border-soft bg-white px-4 py-2.5 outline-none focus:border-forest text-sm"
              />
            ) : f.type === 'select' ? (
              <select
                value={form[f.name]}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                className="rounded-xl border border-border-soft bg-white px-4 py-2.5 outline-none focus:border-forest text-sm"
              >
                <option value="">—</option>
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === 'checkbox' ? (
              <label className="inline-flex items-center gap-2 text-sm text-ink-soft">
                <input
                  type="checkbox"
                  checked={!!form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                />
                {f.label}
              </label>
            ) : f.type === 'number' ? (
              <input
                type="number"
                step="0.01"
                required={f.required}
                value={form[f.name]}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                className="rounded-xl border border-border-soft bg-white px-4 py-2.5 outline-none focus:border-forest text-sm"
              />
            ) : f.type === 'image' ? (
              <ImageField
                value={form[f.name]}
                onChange={(v) => setForm({ ...form, [f.name]: v })}
                testid={`field-${f.name}`}
              />
            ) : (
              <input
                type="text"
                required={f.required}
                value={form[f.name]}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                className="rounded-xl border border-border-soft bg-white px-4 py-2.5 outline-none focus:border-forest text-sm"
              />
            )}
          </div>
        ))}
        {err && <p className="text-red-700 text-sm">{err}</p>}
        <div className="flex gap-3 mt-2">
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="modal-save">
            {busy ? '…' : <>Enregistrer <Save size={16} /></>}
          </button>
          <button type="button" onClick={onClose} className="btn-outline">Annuler</button>
        </div>
      </form>
    </div>
  );
}

function MessagesPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get('/admin/contact-messages').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    await api.delete(`/admin/contact-messages/${id}`);
    load();
  };
  return (
    <div className="grid gap-3" data-testid="messages-panel">
      {items.length === 0 && <p className="text-ink-muted text-sm">Aucun message.</p>}
      {items.map((m) => (
        <div key={m.id} className="bg-white border border-border-soft rounded-2xl p-5">
          <div className="flex justify-between gap-4">
            <div>
              <p className="font-heading text-lg">{m.name} <span className="text-ink-muted text-sm font-body">· {m.email}</span></p>
              {m.subject && <p className="text-sm text-ink-soft mt-0.5">{m.subject}</p>}
            </div>
            <button onClick={() => remove(m.id)} className="text-red-600 hover:text-red-800"><Trash2 size={18} /></button>
          </div>
          <p className="text-ink-soft mt-3 whitespace-pre-line">{m.message}</p>
          <p className="text-xs text-ink-muted mt-3 font-mono">{new Date(m.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function PartnershipsPanel() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const load = async () => {
    try {
      const r = await api.get('/admin/partnership-applications');
      setItems(r.data); setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    }
  };
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      await api.delete(`/admin/partnership-applications/${id}`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de suppression');
    }
  };
  return (
    <div className="grid gap-3" data-testid="partnerships-panel">
      {error && <p className="text-red-700 text-sm" data-testid="partnerships-error">{error}</p>}
      {items.length === 0 && !error && <p className="text-ink-muted text-sm">Aucune candidature.</p>}
      {items.map((m) => (
        <div key={m.id} className="bg-white border border-border-soft rounded-2xl p-5">
          <div className="flex justify-between gap-4 flex-wrap">
            <div>
              <p className="font-heading text-lg">{m.organization}</p>
              <p className="text-sm text-ink-soft">{m.contact_name}{m.role ? ` · ${m.role}` : ''} · {m.email}</p>
              {m.country && <p className="text-xs text-ink-muted">{m.country} {m.website ? `· ${m.website}` : ''}</p>}
            </div>
            <button onClick={() => remove(m.id)} className="text-red-600 hover:text-red-800"><Trash2 size={18} /></button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-xs px-3 py-1 rounded-full bg-forest/10 text-forest">{m.partner_type}</span>
            {m.budget_range && <span className="text-xs px-3 py-1 rounded-full bg-earth/10 text-earth">Budget: {m.budget_range}</span>}
            {m.timeline && <span className="text-xs px-3 py-1 rounded-full bg-sand text-ink-soft">{m.timeline}</span>}
            {(m.focus_areas || []).map((f) => (
              <span key={f} className="text-xs px-3 py-1 rounded-full bg-sand text-ink-soft">{f}</span>
            ))}
          </div>
          <p className="text-ink-soft mt-3 whitespace-pre-line">{m.message}</p>
          <p className="text-xs text-ink-muted mt-3 font-mono">{new Date(m.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function CrudPanel({ section, lng }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => api.get(`/admin/${section.key}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [section.key]);

  const remove = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    await api.delete(`/admin/${section.key}/${id}`);
    load();
  };

  const itemTitle = (item) =>
    item[`title_${lng}`] || item[`name_${lng}`] || item[`label_${lng}`] || item.name || item.title_fr || item.name_fr || item.label_fr || item.key || item.image || item.id;

  const resolveImg = (src) => {
    if (!src) return '';
    if (src.startsWith('/api/uploads/')) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
    return src;
  };

  return (
    <div data-testid={`crud-${section.key}`}>
      <div className="flex justify-end mb-5">
        <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid={`add-${section.key}`}>
          Ajouter <Plus size={16} />
        </button>
      </div>
      <div className="grid gap-3">
        {items.length === 0 && <p className="text-ink-muted text-sm">Aucun élément.</p>}
        {items.map((it) => (
          <div key={it.id} className="bg-white border border-border-soft rounded-2xl p-5 flex justify-between items-center gap-4">
            <div className="flex items-center gap-4 min-w-0">
              {(it.image || it.cover_image || it.logo) && (
                <img src={resolveImg(it.image || it.cover_image || it.logo)} alt="" className="w-14 h-14 rounded-xl object-cover" />
              )}
              <div className="min-w-0">
                <p className="font-heading text-lg truncate">{itemTitle(it)}</p>
                <p className="text-xs text-ink-muted font-mono">{it.id.slice(0, 8)}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(it)} className="text-forest hover:text-ink"><Pencil size={18} /></button>
              <button onClick={() => remove(it.id)} className="text-red-600 hover:text-red-800"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>
      {(showAdd || editing) && (
        <EditModal
          section={section}
          item={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function BookingsPanel() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const load = async () => {
    try {
      const r = await api.get('/admin/experience-bookings');
      setItems(r.data); setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    }
  };
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/experience-bookings/${id}`, { status }); load(); }
    catch (e) { setError(e.response?.data?.detail || 'Erreur'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await api.delete(`/admin/experience-bookings/${id}`); load(); }
    catch (e) { setError(e.response?.data?.detail || 'Erreur'); }
  };
  const STATUS_COLORS = {
    new: 'bg-sun/20 text-ink',
    confirmed: 'bg-forest/15 text-forest',
    done: 'bg-sand text-ink-muted',
    cancelled: 'bg-red-100 text-red-700',
  };
  return (
    <div className="grid gap-3" data-testid="bookings-panel">
      {error && <p className="text-red-700 text-sm">{error}</p>}
      {items.length === 0 && !error && <p className="text-ink-muted text-sm">Aucune réservation.</p>}
      {items.map((b) => (
        <div key={b.id} className="bg-white border border-border-soft rounded-2xl p-5">
          <div className="flex justify-between gap-4 flex-wrap">
            <div>
              <p className="font-heading text-lg">{b.experience_title || '—'}</p>
              <p className="text-sm text-ink-soft">{b.name} · {b.email}{b.phone ? ` · ${b.phone}` : ''}</p>
              <p className="text-xs text-ink-muted">{[b.organization, b.country].filter(Boolean).join(' · ')}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-1 ${STATUS_COLORS[b.status] || ''}`}>
                {b.status}
              </span>
              <select
                value={b.status}
                onChange={(e) => setStatus(b.id, e.target.value)}
                className="text-xs border border-border-soft rounded-lg px-2 py-1 bg-white"
              >
                <option value="new">new</option>
                <option value="confirmed">confirmed</option>
                <option value="done">done</option>
                <option value="cancelled">cancelled</option>
              </select>
              <button onClick={() => remove(b.id)} className="text-red-600 hover:text-red-800"><Trash2 size={18} /></button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-ink-muted">
            <span>Groupe: {b.group_size}</span>
            {b.preferred_dates && <span>Dates: {b.preferred_dates}</span>}
          </div>
          {b.message && <p className="text-ink-soft mt-3 whitespace-pre-line">{b.message}</p>}
          <p className="text-xs text-ink-muted mt-3 font-mono">{new Date(b.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

function OrdersPanel() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get('/admin/orders').then((r) => setItems(r.data)).catch((e) => {
      setError(e.response?.data?.detail || 'Erreur');
    });
  }, []);
  return (
    <div className="grid gap-3" data-testid="orders-panel">
      {error && <p className="text-red-700 text-sm">{error}</p>}
      {items.length === 0 && !error && <p className="text-ink-muted text-sm">Aucune commande.</p>}
      {items.map((o) => (
        <div key={o.session_id} className="bg-white border border-border-soft rounded-2xl p-5">
          <div className="flex justify-between gap-4 flex-wrap">
            <div>
              <p className="font-heading text-lg">{(o.amount || 0).toFixed(2)} {(o.currency || 'eur').toUpperCase()}</p>
              <p className="text-xs text-ink-muted font-mono">{o.session_id?.slice(0, 24)}…</p>
              {o.customer_email && <p className="text-sm text-ink-soft mt-1">{o.customer_email}</p>}
            </div>
            <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-1 ${
              o.payment_status === 'paid' ? 'bg-leaf/15 text-leaf' :
              o.status === 'expired' || o.status === 'canceled' ? 'bg-red-100 text-red-700' :
              'bg-sun/20 text-ink'
            }`}>
              {o.payment_status || o.status || 'pending'}
            </span>
          </div>
          {(o.items || []).length > 0 && (
            <ul className="mt-3 grid gap-1 text-sm text-ink-soft">
              {o.items.map((it) => (
                <li key={`${it.product_id || it.name}-${it.qty}`}>{it.qty} × {it.name} <span className="text-ink-muted">— {(it.unit_price_eur).toFixed(2)} €</span></li>
              ))}
            </ul>
          )}
          <p className="text-xs text-ink-muted mt-3 font-mono">{new Date(o.created_at).toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const { user, logout, checking } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState('articles');

  useEffect(() => {
    if (!checking && !user) nav('/admin/login');
  }, [user, checking, nav]);

  if (checking || !user) return <div className="min-h-screen grid place-items-center">…</div>;

  const allTabs = [
    ...SECTIONS.map((s) => ({ key: s.key, label: t(s.label) })),
    { key: 'messages', label: t('admin.messages') },
    { key: 'partnerships', label: 'Candidatures partenariat' },
    { key: 'bookings', label: 'Réservations' },
    { key: 'orders', label: 'Commandes' },
  ];

  return (
    <main className="min-h-screen bg-sand/40 pt-32 pb-20" data-testid="admin-dashboard">
      <div className="container-x max-w-7xl mx-auto">
        <div className="flex justify-between items-end gap-4 mb-10">
          <div>
            <p className="overline">Lawatan CMS</p>
            <h1 className="font-heading text-4xl sm:text-5xl text-ink tracking-tight">{t('admin.dashboard')}</h1>
            <p className="text-ink-muted text-sm mt-1">{user.email}</p>
          </div>
          <button onClick={() => { logout(); nav('/admin/login'); }} className="btn-outline" data-testid="admin-logout">
            <LogOut size={16} /> {t('admin.logout')}
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-border-soft">
          {allTabs.map((it) => (
            <button
              key={it.key}
              onClick={() => setTab(it.key)}
              data-testid={`admin-tab-${it.key}`}
              className={`text-sm px-4 py-3 border-b-2 transition-all ${
                tab === it.key ? 'border-forest text-forest' : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>

        {tab === 'messages' ? (
          <MessagesPanel />
        ) : tab === 'partnerships' ? (
          <PartnershipsPanel />
        ) : tab === 'bookings' ? (
          <BookingsPanel />
        ) : tab === 'orders' ? (
          <OrdersPanel />
        ) : (
          <CrudPanel section={SECTIONS.find((s) => s.key === tab)} lng={lng} />
        )}
      </div>
    </main>
  );
}
